import { ComponentsManager, Component, EventBus } from '../core/smpl-components.js'
import { StickyHeader } from '../modules/sticky-header.js'
import { OverlayInteractionListener } from '../utils/overlay-interaction-listener.js'
import { gsap } from 'gsap'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

/** Overlay menu
 *
 * Drives the fullscreen main-nav overlay (`.js-nav-overlay`). CSS owns the
 * layout at all times — the panel is fixed to the viewport with its items
 * centred — and its resting state is hidden (`visibility:hidden; opacity:0`).
 * GSAP only drives the transition: the background fades in first, then the menu
 * items stagger up underneath it.
 *
 * On close-complete every inline prop GSAP wrote is cleared, so the hidden
 * state falls back to CSS and window-resize needs no JS at all.
 */
class OverlayMenu {
	constructor(el, options) {
		this.element = el // .js-main-nav (component root)
		this.options = Object.assign({}, options)

		this.elOverlay = this.element.querySelector('.js-nav-overlay')
		this.elToggle = this.element.querySelector('.js-toggle')

		// Stagger targets: the inner span of every menu link (the span is the
		// transform handle, so the link keeps its own hit area) + the language
		// pill trailing the list.
		this.elItems = [
			...this.elOverlay.querySelectorAll('.menu-item__link > span'),
			...this.elOverlay.querySelectorAll('.nav-overlay__language'),
		]

		this.isVisible = false
		this.scrollLocked = false
		this.tl = null

		this.bndToggle = this.toggle.bind(this)
		this.bndOnOverlayClick = this.onOverlayClick.bind(this)

		// Escape only — the overlay covers the viewport, so there is no
		// "outside" left to click; background clicks are handled below.
		this.interactionListener = new OverlayInteractionListener({
			element: this.element,
			keys: ['Escape'],
			enabled: false,
			onKeyPress: () => this.hide(),
		})

		this.elOverlay.setAttribute('aria-hidden', 'true')
		this.elToggle.setAttribute('aria-expanded', 'false')

		this.elToggle.addEventListener('click', this.bndToggle)
		this.elOverlay.addEventListener('click', this.bndOnOverlayClick)
	}

	toggle(e) {
		if (this.isVisible) {
			this.hide(e)
		} else {
			this.show(e)
		}
	}

	show(e) {
		if (this.isVisible) return
		if (e) {
			e.preventDefault()
			e.stopPropagation()
		}
		this.isVisible = true

		if (this.tl) this.tl.kill()

		if (this.options.onToggle) this.options.onToggle(true)

		// `is-expanded` is the instant toggle state (it morphs the burger into
		// a cross); `is-overlay-active` tracks the panel itself and outlives it
		// until the close animation is done.
		this.element.classList.add('is-expanded', 'is-overlay-active')
		this.elOverlay.setAttribute('aria-hidden', 'false')
		this.elToggle.setAttribute('aria-expanded', 'true')

		// The overlay covers the viewport, so the page behind it must not
		// scroll. Pass the overlay so its own overflow stays usable when the
		// menu is taller than the screen.
		if (!this.scrollLocked) {
			this.scrollLocked = true
			window.app.browser.disableScroll(this.elOverlay)
		}

		this.elOverlay.scrollTop = 0

		this.tl = gsap.timeline()
		this.tl.fromTo(this.elOverlay, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.45, ease: 'power2.out' })
		this.tl.fromTo(
			this.elItems,
			{ autoAlpha: 0, yPercent: 60 },
			{
				autoAlpha: 1,
				yPercent: 0,
				duration: 0.7,
				ease: 'power3.out',
				stagger: 0.06,
			},
			'-=0.25',
		)

		this.interactionListener.enable()
	}

	hide(e) {
		if (!this.isVisible) return
		if (e) {
			e.preventDefault()
			e.stopPropagation()
		}
		this.isVisible = false

		if (this.tl) this.tl.kill()

		if (this.options.onToggle) this.options.onToggle(false)

		// Flip the burger back the moment the close starts — it reads as the
		// cause of the panel leaving, not as a report that it has left.
		this.element.classList.remove('is-expanded')
		this.elToggle.setAttribute('aria-expanded', 'false')

		this.tl = gsap.timeline({
			onComplete: () => {
				this.element.classList.remove('is-overlay-active')
				this.elOverlay.setAttribute('aria-hidden', 'true')
				// Drop every inline prop so the CSS resting state (hidden)
				// takes over again.
				gsap.set([this.elOverlay, ...this.elItems], { clearProps: 'all' })
				if (this.scrollLocked) {
					this.scrollLocked = false
					window.app.browser.enableScroll()
				}
			},
		})
		this.tl.to(this.elItems, {
			autoAlpha: 0,
			yPercent: -30,
			duration: 0.25,
			ease: 'power2.in',
			stagger: 0.02,
		})
		this.tl.to(this.elOverlay, { autoAlpha: 0, duration: 0.35, ease: 'power2.inOut' }, '-=0.15')

		this.interactionListener.disable()
	}

	onOverlayClick(e) {
		// Only a link closes the overlay (Swup handles the navigation itself).
		// Clicking the background does nothing — use the burger or Escape.
		const link = e.target.closest('a')
		if (link && link.href) this.hide()
	}

	destroy() {
		if (this.tl) this.tl.kill()
		this.elToggle.removeEventListener('click', this.bndToggle)
		this.elOverlay.removeEventListener('click', this.bndOnOverlayClick)
		this.interactionListener.destroy()
		if (this.scrollLocked) {
			this.scrollLocked = false
			window.app.browser.enableScroll()
		}
	}
}

class OverlayHandler {
	constructor(element) {
		this.overlayMenu = new OverlayMenu(element, {
			onToggle: (state) => {},
		})
	}

	hide() {
		this.overlayMenu.hide()
	}

	show() {
		this.overlayMenu.show()
	}

	destroy() {
		this.overlayMenu.destroy()
	}
}

/** Language toggle
 *
 * The pill is a single link to the other language. On click we don't prevent
 * navigation — we just flip the active index so the knob slides to the other
 * side during the page transition.
 */
class LanguageToggle {
	constructor(element) {
		this.element = element
		this.bndOnClick = this.onClick.bind(this)
		this.element.addEventListener('click', this.bndOnClick)
	}
	onClick() {
		const current = Number(this.element.dataset.active || 0)
		this.element.dataset.active = current === 0 ? 1 : 0
	}
	destroy() {
		this.element.removeEventListener('click', this.bndOnClick)
	}
}

//
export default class extends Component {
	prepare() {
		// -- Foldout / burger toggle -------------------------- //
		this.overlayHandler = new OverlayHandler(this.element)

		// // -- Sticky Header ------------------------------------ //
		this.stickyHeader = new StickyHeader({
			element: this.element,
			offset: 20,
			showAtBottom: false,
			thresholds: { reposition: 100 },
			on: {
				stateChange: (newState, oldState, ctx) => {
					// console.log(`Header state: ${oldState} → ${newState}`)
					// document.body.classList.remove(`header-state-${oldState}`)
					// document.body.classList.add(`header-state-${newState}`)
				},
				'state:top': (ctx) => {},
				'state:hidden': (ctx) => {
					this.overlayHandler.hide()
				},
				'threshold:compact': ({ crossed }) => {},
				'threshold:cta': ({ crossed }) => {},
			},
		})
	}

	mount() {}

	unmount() {
		if (this.overlayHandler) {
			this.overlayHandler.destroy()
		}

		if (this.languageToggle) {
			this.languageToggle.destroy()
		}

		if (this.stickyHeader) {
			this.stickyHeader.destroy()
		}
	}

	onSwupNavSelfClick(visit) {
		if (this.navIsland) {
			this.navIsland.collapse()
		}
	}
	onNavShapeClick(e) {
		// if (this.overlayMenu.isVisible) return
		// this.overlayMenu.show()
	}
}
